import { Card, CardHeader, CardTitle, CardContent } from '../components/common/Card';
import { Badge } from '../components/common/Badge';
import { Button } from '../components/common/Button';
import { 
  CalendarDaysIcon, 
  ChartBarIcon, 
  ClockIcon,
  UserGroupIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon as PendingIcon,
  DocumentTextIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { LeaveBalance } from '../components/leaves/LeaveBalance';
import { Link } from 'react-router-dom';
import { Alert } from '../components/common/Alert';
import { usePermissions } from '../hooks/usePermissions';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { format } from 'date-fns';
import { pl, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

// Helper function to format dates
const formatDate = (dateString: string | null | undefined, locale?: string) => {
  if (!dateString) return '-';
  try {
    // Handle different date formats (YYYY-MM-DD or ISO)
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const localeObj = locale === 'pl' ? pl : enUS;
    return format(date, 'dd.MM.yyyy', { locale: localeObj });
  } catch (error) {
    console.error('Date formatting error:', error, 'for date:', dateString);
    return '-';
  }
};

// Helper function to parse date from backend (ISO format)
const parseDateFromBackend = (dateString: string | null) => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return date;
  } catch {
    return null;
  }
};

export const Dashboard = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const { canViewAllLeaves, canApproveLeaves, isManager, isAdmin, isHR } = usePermissions();
  const { t, i18n } = useTranslation();
  
  // Debug i18n loading
  console.log('Dashboard i18n ready:', i18n.isInitialized, 'language:', i18n.language);
  
  // Helper function to calculate working days
  const calculateWorkingDays = (startDate: string, endDate: string): number => {
    if (!startDate || !endDate) return 0;
    
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      let count = 0;
      const current = new Date(start);
      
      while (current <= end) {
        const dayOfWeek = current.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          count++;
        }
        current.setDate(current.getDate() + 1);
      }
      
      return count;
    } catch (error) {
      return 0;
    }
  };
  
  // Fetch real data from API
  const { data: leaveRequests, error: leaveRequestsError } = useQuery({
    queryKey: ['dashboardLeaveRequests', user?.id],
    queryFn: async () => {
      const params = canViewAllLeaves ? {} : { userId: user?.id };
      const response = await api.get('/leaves', { params });
      return response.data.leaveRequests || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: 'Error Loading Leave Requests',
        message: 'Failed to load leave requests. Please try refreshing the page.'
      });
    }
  });
  
  const { data: teamMembers } = useQuery({
    queryKey: ['dashboardTeamMembers'],
    queryFn: async () => {
      const response = await api.get('/users/team-members');
      return response.data.users || [];
    },
    enabled: isManager || isHR || isAdmin,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false
  });
  
  const { data: leaveStats } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: async () => {
      const response = await api.get('/reports/summary');
      return response.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false
  });

  const { data: substitutions } = useQuery({
    queryKey: ['dashboardSubstitutions'],
    queryFn: async () => {
      try {
        const response = await api.get('/substitutions/my');
        return response.data;
      } catch (error) {
        console.error('Error fetching substitutions:', error);
        return null;
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    retry: false // Nie próbuj ponownie jeśli endpoint nie istnieje
  });

  const getStatusBadge = (status: string) => {
    console.log('Status badge for:', status);
    
    const variants: Record<string, string> = {
      'draft': 'gray',
      'pending': 'warning',
      'approved': 'success',
      'rejected': 'error',
      'cancelled': 'secondary',
      'submitted': 'warning',
      'oczekujący': 'warning',
      'zatwierdzony': 'success',
      'odrzucony': 'error',
      'anulowany': 'secondary',
      'złożony': 'warning'
    };
    
    const labels: Record<string, string> = {
      'draft': 'Szkic',
      'pending': 'Oczekujący',
      'approved': 'Zatwierdzony',
      'rejected': 'Odrzucony',
      'cancelled': 'Anulowany',
      'submitted': 'Złożony',
      'oczekujący': 'Oczekujący',
      'zatwierdzony': 'Zatwierdzony',
      'odrzucony': 'Odrzucony',
      'anulowany': 'Anulowany',
      'złożony': 'Złożony'
    };
    
    const normalizedStatus = status?.toLowerCase();
    const variant = variants[normalizedStatus] || variants[status] || 'gray';
    const label = labels[normalizedStatus] || labels[status] || status;
    
    return <Badge variant={variant as any}>{label}</Badge>;
  };

  const getStatusIcon = (status: string) => {
    const normalizedStatus = status?.toLowerCase();
    
    if (['approved', 'zatwierdzony'].includes(normalizedStatus)) {
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    }
    
    if (['rejected', 'odrzucony'].includes(normalizedStatus)) {
      return <XCircleIcon className="h-5 w-5 text-red-500" />;
    }
    
    if (['pending', 'submitted', 'oczekujący', 'złożony'].includes(normalizedStatus)) {
      return <PendingIcon className="h-5 w-5 text-yellow-500" />;
    }
    
    return null;
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          {t('dashboard.welcome', { name: user?.first_name || 'User' })}
        </h1>
        <div className="mt-2 text-sm text-gray-600">
          {user?.position && (
            <span className="inline-flex items-center px-2 py-1 mr-2 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
              💼 {user.position}
            </span>
          )}
          {user?.teams && user.teams.length > 0 && (
            <span className="inline-flex items-center px-2 py-1 mr-2 text-xs font-medium bg-green-100 text-green-800 rounded-full">
              👥 {user.teams[0].name}
            </span>
          )}
          {user?.departments && user.departments.length > 0 && (
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
              🏢 {user.departments[0].name}
            </span>
          )}
        </div>
        <p className="mt-3 text-sm text-gray-500">
          {isAdmin ? 'Zarządzaj systemem urlopów całej firmy' : t('dashboard.overview')}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarDaysIcon className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('dashboard.stats.totalLeaves')}
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {isAdmin ? (leaveStats?.totalLeaves || leaveRequests?.length || 0) : (leaveRequests?.length || 0)}
                  </dd>
                  <dd className="text-sm text-gray-500">{t('dashboard.stats.thisYear')}</dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('dashboard.stats.pendingLeaves')}
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {leaveStats?.pendingLeaves || 0}
                  </dd>
                  <dd className="text-sm text-gray-500">{t('dashboard.stats.awaitingApproval')}</dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserGroupIcon className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('dashboard.stats.totalUsers')}
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {leaveStats?.totalUsers || 0}
                  </dd>
                  <dd className="text-sm text-gray-500">{t('dashboard.stats.activeEmployees')}</dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('dashboard.stats.approvedThisMonth')}
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {leaveStats?.approvedLeavesThisMonth || 0}
                  </dd>
                  <dd className="text-sm text-gray-500">{t('dashboard.stats.leaveRequests')}</dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Recent Requests */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{t('dashboard.recentRequests')}</CardTitle>
              <Link to="/leaves">
                <Button size="sm" variant="outline">
                  {t('dashboard.viewAll')}
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {!leaveRequests || leaveRequests.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">{t('dashboard.noRequests')}</p>
            ) : (
              <ul className="divide-y divide-gray-200">
                {leaveRequests.slice(0, 5).map((request: any) => (
                  <li key={request.id} className="py-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        {getStatusIcon(request.status)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {request.leaveType?.name || 'Leave Request'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatDate(request.start_date || request.startDate, i18n.language)} - {formatDate(request.end_date || request.endDate, i18n.language)} ({calculateWorkingDays(request.start_date || request.startDate, request.end_date || request.endDate)} {t('leaves.days')})
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        {getStatusBadge(request.status)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Team Status - Show only for managers, HR, and admins */}
        {(isManager || isHR || isAdmin) && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t('dashboard.teamStatus', 'Status zespołu')}</CardTitle>
                <Link to="/team">
                  <Button size="sm" variant="outline">
                    {t('dashboard.viewTeam', 'Zobacz zespół')}
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {!teamMembers || teamMembers.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">{t('dashboard.noTeamMembers', 'Brak członków zespołu')}</p>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {teamMembers.slice(0, 5).map((member: any) => (
                    <li key={member.id} className="py-4">
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gray-500">
                            <span className="text-sm font-medium leading-none text-white">
                              {(member.firstName || member.first_name)?.[0]}{(member.lastName || member.last_name)?.[0]}
                            </span>
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {member.firstName || member.first_name} {member.lastName || member.last_name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {member.department?.name || t('team.noDepartment', 'Brak działu')}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <Badge variant="success">{t('team.status.present', 'Obecny')}</Badge>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Substitutions Section */}
      {substitutions && (substitutions.responsibilities?.length > 0 || substitutions.requests?.length > 0) && (
        <div className="mt-8">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t('substitutions.title')}</CardTitle>
                <Link to="/substitutions">
                  <Button size="sm" variant="outline">
                    {t('dashboard.viewAll')}
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Pending substitution responsibilities */}
                {substitutions.responsibilities?.filter((s: any) => s.status === 'pending').length > 0 && (
                  <Alert variant="warning">
                    <p className="font-medium">{t('substitutions.pending')}</p>
                    <p className="text-sm">
                      {substitutions.responsibilities.filter((s: any) => s.status === 'pending').length} {t('substitutions.requestedBy', { name: '' }).replace(' {{name}}', '')}
                    </p>
                  </Alert>
                )}
                
                {/* Active substitutions */}
                {substitutions.requests?.filter((s: any) => s.status === 'accepted').length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">{t('substitutions.active')}</h4>
                    <ul className="space-y-2">
                      {substitutions.requests
                        .filter((s: any) => s.status === 'accepted')
                        .slice(0, 3)
                        .map((sub: any) => (
                          <li key={sub.id} className="flex items-center justify-between text-sm">
                            <span>
                              {t('substitutions.substituteFor', { name: `${sub.substitute?.firstName || sub.substitute?.first_name} ${sub.substitute?.lastName || sub.substitute?.last_name}` })}
                            </span>
                            <Badge variant="success">{t('substitutions.status.accepted')}</Badge>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Leave Balance Section */}
      <div className="mt-8">
        <LeaveBalance />
      </div>

      {/* Quick Actions */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Urlopy - Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Szybkie akcje - Urlopy</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Link to="/leaves/new">
                <Button className="w-full justify-start">
                  <DocumentDuplicateIcon className="h-5 w-5 mr-2" />
                  Nowy wniosek urlopowy
                </Button>
              </Link>
              <Link to="/team-calendar">
                <Button variant="outline" className="w-full justify-start">
                  <CalendarDaysIcon className="h-5 w-5 mr-2" />
                  Kalendarz zespołu
                </Button>
              </Link>
              <Link to="/reports">
                <Button variant="outline" className="w-full justify-start">
                  <ChartBarIcon className="h-5 w-5 mr-2" />
                  Raporty urlopowe
                </Button>
              </Link>
              <Link to="/team">
                <Button variant="outline" className="w-full justify-start">
                  <UserGroupIcon className="h-5 w-5 mr-2" />
                  Katalog zespołu
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Księgowość - Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Szybkie akcje - Księgowość</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Link to="/accounting/invoices">
                <Button variant="outline" className="w-full justify-start">
                  <DocumentTextIcon className="h-5 w-5 mr-2" />
                  Faktury
                </Button>
              </Link>
              <Link to="/accounting/payments">
                <Button variant="outline" className="w-full justify-start">
                  <CreditCardIcon className="h-5 w-5 mr-2" />
                  Płatności
                </Button>
              </Link>
              <Link to="/accounting/overdue">
                <Button variant="outline" className="w-full justify-start">
                  <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                  Zaległości
                </Button>
              </Link>
              <Link to="/accounting/reports">
                <Button variant="outline" className="w-full justify-start">
                  <CurrencyDollarIcon className="h-5 w-5 mr-2" />
                  Raporty finansowe
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};